import * as Sentry from "@sentry/react";
import {
	type GetJourneyProgressResponse,
	getJourneyProgressResponseSchema,
	type StepStatePayload,
	type TaskProgressPayload,
} from "../domain/journeyProgress/schemas";
import { api } from "../lib/api";

/**
 * M011/S01 T06: era-web wrapper around the new /api/journey-progress endpoints
 * (era-api routes/journeyProgress.ts). Every call goes through the centralized
 * `api` client per the CLAUDE.md architecture rule — never fetch() directly.
 *
 * The mutation endpoints return only the row they touched (`{ taskProgress }`
 * or `{ stepState }`) so callers/optimistic-update paths can patch the
 * in-memory cache without re-fetching the whole GET payload. The full re-fetch
 * happens via React Query invalidation in `onSettled` (see useJourneyProgress).
 *
 * All response bodies are zod-parsed at this boundary. Parse failures emit a
 * Sentry breadcrumb (no PHI) and rethrow so React Query surfaces an error
 * state to the consumer.
 */

const TASK_MUTATION_RESPONSE_KEY = "taskProgress";
const STEP_MUTATION_RESPONSE_KEY = "stepState";

function breadcrumbParseFailure(endpoint: string, error: unknown): void {
	Sentry.addBreadcrumb({
		category: "journey-progress",
		message: `Malformed response from ${endpoint}`,
		level: "error",
		data: {
			error: error instanceof Error ? error.message : String(error),
		},
	});
}

export async function getJourneyProgress(
	journeyType: string,
): Promise<GetJourneyProgressResponse> {
	const response = await api.get<unknown>("/api/journey-progress", {
		params: { journeyType },
	});
	const parsed = getJourneyProgressResponseSchema.safeParse(response.data);
	if (!parsed.success) {
		breadcrumbParseFailure("GET /api/journey-progress", parsed.error);
		throw new Error("Journey progress response failed schema validation");
	}
	return parsed.data;
}

function parseTaskMutation(
	endpoint: string,
	data: unknown,
): TaskProgressPayload {
	if (
		typeof data !== "object" ||
		data === null ||
		!(TASK_MUTATION_RESPONSE_KEY in data)
	) {
		breadcrumbParseFailure(endpoint, "missing taskProgress field");
		throw new Error(`${endpoint} response missing taskProgress`);
	}
	// Cross-check against the response schema's task field via the canonical parser.
	// Re-using the embedded schema keeps this module dependency-free.
	const value = (data as Record<string, unknown>)[TASK_MUTATION_RESPONSE_KEY];
	const parsed =
		getJourneyProgressResponseSchema.shape.taskProgress.element.safeParse(
			value,
		);
	if (!parsed.success) {
		breadcrumbParseFailure(endpoint, parsed.error);
		throw new Error(`${endpoint} taskProgress failed schema validation`);
	}
	return parsed.data;
}

function parseStepMutation(endpoint: string, data: unknown): StepStatePayload {
	if (
		typeof data !== "object" ||
		data === null ||
		!(STEP_MUTATION_RESPONSE_KEY in data)
	) {
		breadcrumbParseFailure(endpoint, "missing stepState field");
		throw new Error(`${endpoint} response missing stepState`);
	}
	const value = (data as Record<string, unknown>)[STEP_MUTATION_RESPONSE_KEY];
	const parsed =
		getJourneyProgressResponseSchema.shape.stepStates.element.safeParse(value);
	if (!parsed.success) {
		breadcrumbParseFailure(endpoint, parsed.error);
		throw new Error(`${endpoint} stepState failed schema validation`);
	}
	return parsed.data;
}

export async function completeTask(input: {
	journeyType: string;
	taskId: string;
	progressPercent?: number;
}): Promise<TaskProgressPayload> {
	const endpoint = `POST /api/journey-progress/tasks/${input.taskId}/complete`;
	const response = await api.post<unknown>(
		`/api/journey-progress/tasks/${encodeURIComponent(input.taskId)}/complete`,
		{
			journeyType: input.journeyType,
			...(input.progressPercent !== undefined
				? { progressPercent: input.progressPercent }
				: {}),
		},
	);
	return parseTaskMutation(endpoint, response.data);
}

export async function reopenTask(input: {
	journeyType: string;
	taskId: string;
}): Promise<TaskProgressPayload> {
	const endpoint = `POST /api/journey-progress/tasks/${input.taskId}/reopen`;
	const response = await api.post<unknown>(
		`/api/journey-progress/tasks/${encodeURIComponent(input.taskId)}/reopen`,
		{ journeyType: input.journeyType },
	);
	return parseTaskMutation(endpoint, response.data);
}

export async function openStep(input: {
	journeyType: string;
	stepId: string;
}): Promise<StepStatePayload> {
	const endpoint = `POST /api/journey-progress/steps/${input.stepId}/open`;
	const response = await api.post<unknown>(
		`/api/journey-progress/steps/${encodeURIComponent(input.stepId)}/open`,
		{ journeyType: input.journeyType },
	);
	return parseStepMutation(endpoint, response.data);
}

export async function closeStep(input: {
	journeyType: string;
	stepId: string;
}): Promise<StepStatePayload> {
	const endpoint = `POST /api/journey-progress/steps/${input.stepId}/close`;
	const response = await api.post<unknown>(
		`/api/journey-progress/steps/${encodeURIComponent(input.stepId)}/close`,
		{ journeyType: input.journeyType },
	);
	return parseStepMutation(endpoint, response.data);
}
